import { useState } from 'react';
import { Sparkles, Send, X, Pencil, Loader2 } from 'lucide-react';

interface DraftReviewCardProps {
  content: string;
  onApprove: (editedContent?: string) => void;
  onDiscard: () => void;
  isApproving?: boolean;
}

/**
 * Renders an AI-generated DRAFT reply (auto-reply-off mode) for human review.
 * The agent can edit the text, then Send it to the customer or Discard it.
 */
export function DraftReviewCard({ content, onApprove, onDiscard, isApproving }: DraftReviewCardProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(content);

  return (
    <div className="ml-auto max-w-[85%] rounded-xl border border-primary/30 bg-primary/5 p-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-primary">
        <Sparkles className="h-3.5 w-3.5" />
        AI draft — review before sending
      </div>

      {editing ? (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          className="w-full resize-y rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      ) : (
        <p className="whitespace-pre-wrap text-sm text-card-foreground">{text}</p>
      )}

      <div className="mt-2.5 flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Pencil className="h-3.5 w-3.5" /> {editing ? 'Done' : 'Edit'}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-error/10 hover:text-error"
        >
          <X className="h-3.5 w-3.5" /> Discard
        </button>
        <button
          type="button"
          onClick={() => onApprove(editing ? text : undefined)}
          disabled={isApproving || !text.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {isApproving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          Send
        </button>
      </div>
    </div>
  );
}

export default DraftReviewCard;
